/**
 * Resource tagging constants for Between Sessions resources
 *
 * Used by both therapist admin and client-facing apps for consistent
 * categorization and filtering of resources.
 */

// ==================== TOPICS ====================
// The emotional flavor or life context — helps specify what emotion
// or where in life something shows up

export const TOPICS = [
  // Emotions
  'Anger', 'Sadness', 'Fear', 'Anxiety', 'Depression', 'Panic', 'Overwhelm',
  'Shame', 'Guilt', 'Grief', 'Jealousy', 'Loneliness', 'Resentment',
  'Frustration', 'Hopelessness', 'Envy', 'Hurt', 'Rejection', 'Abandonment',
  'Insecurity', 'Emptiness', 'Numbness', 'Regret', 'Embarrassment',
  'Disappointment', 'Worry',
  // Life Contexts
  'Relationships', 'Family', 'Work', 'Parenting', 'Identity', 'Health',
  'Friendship', 'Career', 'School', 'Finances', 'Spirituality', 'Sexuality',
  'Body Image', 'Aging', 'Caregiving', 'Divorce', 'Dating', 'Marriage',
  'Pregnancy', 'Postpartum', 'Infertility', 'Chronic Illness', 'Disability',
  'Loss of a Loved One', 'Pet Loss', 'Life Transitions', 'Retirement',
  'Empty Nest', 'Trauma', 'Addiction', 'Recovery',
  'Intimacy', 'Trust', 'Boundaries', 'Self-Worth', 'Loss', 'Burnout',
  'Communication', 'Attachment', 'Self-Care', 'Self-Esteem', 'Sleep',
  'Abuse', 'Domestic Violence', 'Childhood', 'LGBTQ+', 'Cultural Identity',
  'Neurodivergence', 'Eating', 'Miscarriage', 'Suicidality', 'Self-Harm',
  'Estrangement', 'Forgiveness', 'Co-Parenting', 'Single Parenting',
  'Blended Family', 'Sibling Relationships', 'In-Laws', 'Social Media',
  'Perfectionism',
] as const

export type Topic = typeof TOPICS[number]

// Subset of topics that are emotions (for filtering UI)
export const EMOTION_TOPICS = [
  'Anger', 'Sadness', 'Fear', 'Anxiety', 'Depression', 'Panic', 'Overwhelm',
  'Shame', 'Guilt', 'Grief', 'Jealousy', 'Loneliness', 'Resentment',
  'Frustration', 'Hopelessness', 'Envy', 'Hurt', 'Rejection', 'Abandonment',
  'Insecurity', 'Emptiness', 'Numbness', 'Regret', 'Embarrassment',
  'Disappointment', 'Worry',
] as const

// Subset of topics that are life contexts (for filtering UI)
export const LIFE_CONTEXT_TOPICS = [
  'Relationships', 'Family', 'Work', 'Parenting', 'Identity', 'Health',
  'Friendship', 'Career', 'School', 'Finances', 'Spirituality', 'Sexuality',
  'Body Image', 'Aging', 'Caregiving', 'Divorce', 'Dating', 'Marriage',
  'Pregnancy', 'Postpartum', 'Infertility', 'Chronic Illness', 'Disability',
  'Loss of a Loved One', 'Pet Loss', 'Life Transitions', 'Retirement',
  'Empty Nest', 'Trauma', 'Addiction', 'Recovery',
  'Intimacy', 'Trust', 'Boundaries', 'Self-Worth', 'Loss', 'Burnout',
  'Communication', 'Attachment', 'Self-Care', 'Self-Esteem', 'Sleep',
  'Abuse', 'Domestic Violence', 'Childhood', 'LGBTQ+', 'Cultural Identity',
  'Neurodivergence', 'Eating', 'Miscarriage', 'Suicidality', 'Self-Harm',
  'Estrangement', 'Forgiveness', 'Co-Parenting', 'Single Parenting',
  'Blended Family', 'Sibling Relationships', 'In-Laws', 'Social Media',
  'Perfectionism',
] as const

// ==================== PRIMARY EXPERIENCES ====================
// The underlying pattern or mechanism — what the client is fundamentally
// struggling with (e.g., overwhelm, avoidance, rumination)
// Note: Standalone emotions (shame, guilt, jealousy, resentment, perfectionism) removed
// as they're already in EMOTION_TOPICS/LIFE_CONTEXT_TOPICS

export const PRIMARY_EXPERIENCES = [
  'overthinking-rumination', 'anxiety-fear', 'strong-emotions-overwhelm', 'numbness-shutdown',
  'fear-of-feelings', 'self-criticism-inner-critic', 'identity-authentic-self',
  'self-trust-decision-difficulty', 'avoidance-procrastination', 'motivation-taking-action',
  'feeling-stuck', 'control-letting-go', 'relationship-tension-conflict', 'connection-loneliness',
  'grief-loss', 'low-mood-heaviness', 'meaning-direction', 'setbacks-relapse',
] as const

export type PrimaryExperience = typeof PRIMARY_EXPERIENCES[number]

// ==================== TOPIC TO PRIMARY EXPERIENCE MAPPING ====================
// Maps emotions/contexts to related primary experiences for cross-filtering
export const TOPIC_TO_PRIMARY_EXPERIENCE: Record<string, string[]> = {
  // Emotions -> Primary Experiences
  'Anxiety': ['anxiety-fear', 'overthinking-rumination'],
  'Fear': ['anxiety-fear', 'fear-of-feelings'],
  'Overwhelm': ['strong-emotions-overwhelm'],
  'Shame': ['self-criticism-inner-critic'],
  'Guilt': ['self-criticism-inner-critic'],
  'Grief': ['grief-loss'],
  'Jealousy': ['relationship-tension-conflict'],
  'Loneliness': ['connection-loneliness'],
  'Resentment': ['relationship-tension-conflict'],
  'Numbness': ['numbness-shutdown'],
  'Depression': ['low-mood-heaviness'],
  'Sadness': ['low-mood-heaviness', 'grief-loss'],
  'Hopelessness': ['low-mood-heaviness', 'meaning-direction'],
  'Worry': ['anxiety-fear', 'overthinking-rumination'],
  // Life Contexts -> Primary Experiences
  'Identity': ['identity-authentic-self'],
  'Loss': ['grief-loss'],
  'Perfectionism': ['self-criticism-inner-critic'],
  'Relationships': ['relationship-tension-conflict', 'connection-loneliness'],
  'Self-Worth': ['self-criticism-inner-critic'],
  'Self-Esteem': ['self-criticism-inner-critic'],
  'Trauma': ['fear-of-feelings', 'numbness-shutdown'],
  'Addiction': ['avoidance-procrastination', 'setbacks-relapse'],
  'Recovery': ['setbacks-relapse', 'motivation-taking-action'],
}

// ==================== MODALITIES ====================
// Therapeutic approaches and frameworks (abbreviated form for resource tagging)

export const RESOURCE_MODALITIES = [
  'ACT', 'CBT', 'DBT', 'EMDR', 'IFS', 'Psychodynamic',
  'Somatic', 'Mindfulness', 'MI', 'SFBT',
  'CFT',
  'Narrative Therapy', 'EFT',
  'Existential Therapy', 'Humanistic', 'IPT', 'Family Systems',
  'Attachment', 'Gottman', 'Imago', 'Art Therapy', 'Play Therapy',
] as const

export type ResourceModality = typeof RESOURCE_MODALITIES[number]

// Full names for modality abbreviations (for tooltips/display)
export const MODALITY_FULL_NAMES: Record<string, string> = {
  'ACT': 'Acceptance and Commitment Therapy',
  'CBT': 'Cognitive Behavioral Therapy',
  'DBT': 'Dialectical Behavior Therapy',
  'EMDR': 'Eye Movement Desensitization and Reprocessing',
  'IFS': 'Internal Family Systems',
  'MI': 'Motivational Interviewing',
  'SFBT': 'Solution-Focused Brief Therapy',
  'CFT': 'Compassion-Focused Therapy',
  'EFT': 'Emotionally Focused Therapy',
  'IPT': 'Interpersonal Therapy',
}

// ==================== INTENTIONS ====================
// What the resource is designed to help the user do

export const INTENTIONS = ['calm', 'understand', 'explore', 'build-skills'] as const

export type Intention = typeof INTENTIONS[number]

export const INTENTION_CONFIG: Record<Intention, { label: string; emoji: string; description: string }> = {
  'calm': { label: 'Find calm', emoji: '🌊', description: 'Soothing, grounding exercises' },
  'understand': { label: 'Understand', emoji: '💡', description: 'Learn about what you\'re experiencing' },
  'explore': { label: 'Explore', emoji: '🔍', description: 'Reflect and discover insights' },
  'build-skills': { label: 'Build skills', emoji: '🛠️', description: 'Practical tools and techniques' },
}

// ==================== INTENSITY ====================
// How deep or challenging the resource is

export const INTENSITIES = ['GENTLE', 'MODERATE', 'DEEPER'] as const

export type Intensity = typeof INTENSITIES[number]

export const INTENSITY_CONFIG: Record<Intensity, { label: string; color: string; description: string }> = {
  GENTLE: { label: 'Gentle', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: 'Light and supportive' },
  MODERATE: { label: 'Moderate', color: 'bg-amber-50 text-amber-700 border-amber-200', description: 'Balanced exploration' },
  DEEPER: { label: 'Deeper', color: 'bg-violet-50 text-violet-700 border-violet-200', description: 'In-depth work' },
}

// ==================== FORMAT ====================
// The type of resource content

export const FORMATS = ['READING', 'GUIDED_EXERCISE', 'WRITING_PROMPT', 'AUDIO_VIDEO'] as const

export type Format = typeof FORMATS[number]

export const FORMAT_CONFIG: Record<Format, { label: string; iconName: string }> = {
  READING: { label: 'Read', iconName: 'BookOpen' },
  GUIDED_EXERCISE: { label: 'Exercise', iconName: 'Sparkles' },
  WRITING_PROMPT: { label: 'Writing', iconName: 'Pencil' },
  AUDIO_VIDEO: { label: 'Watch', iconName: 'Video' },
}

// ==================== SEARCH SYNONYMS ====================
// Maps natural language search terms to topics, primary experiences, and intentions

export const SEARCH_SYNONYMS: Record<string, string[]> = {
  // Emotions
  'stressed': ['Anxiety', 'Overwhelm', 'Burnout', 'Work'],
  'anxious': ['Anxiety', 'Fear', 'Panic', 'Worry'],
  'sad': ['Sadness', 'Depression', 'Grief', 'Hopelessness'],
  'depressed': ['Depression', 'Sadness', 'Hopelessness', 'Numbness'],
  'angry': ['Anger', 'Frustration', 'Resentment'],
  'lonely': ['Loneliness', 'Friendship', 'Relationships'],
  'scared': ['Fear', 'Anxiety', 'Panic'],
  'overwhelmed': ['Overwhelm', 'Anxiety', 'Burnout'],
  'nervous': ['Anxiety', 'Fear', 'Panic'],
  'worried': ['Worry', 'Anxiety', 'Fear'],
  'guilty': ['Guilt', 'Shame'],
  'ashamed': ['Shame', 'Guilt', 'Embarrassment'],
  'jealous': ['Jealousy', 'Envy', 'Insecurity'],
  'hurt': ['Hurt', 'Rejection', 'Abandonment'],
  'stuck': ['feeling-stuck', 'avoidance-procrastination', 'motivation-taking-action'],
  'lost': ['Identity', 'meaning-direction', 'Life Transitions', 'identity-authentic-self'],
  'empty': ['Emptiness', 'Numbness', 'Depression', 'numbness-shutdown'],
  'numb': ['Numbness', 'Emptiness', 'numbness-shutdown'],
  'overthinking': ['overthinking-rumination', 'Anxiety', 'Worry'],
  'ruminating': ['overthinking-rumination', 'Anxiety'],
  // Intentions
  'relax': ['calm'],
  'calm down': ['calm'],
  'chill': ['calm'],
  'peace': ['calm'],
  'learn': ['understand'],
  'why': ['understand'],
  'help': ['build-skills', 'calm'],
  'tools': ['build-skills'],
  'cope': ['build-skills', 'calm'],
  'deal with': ['build-skills'],
  // Life contexts
  'job': ['Work', 'Career', 'Burnout'],
  'partner': ['Relationships', 'Marriage', 'Intimacy'],
  'spouse': ['Relationships', 'Marriage', 'Communication'],
  'kids': ['Parenting', 'Family'],
  'children': ['Parenting', 'Family'],
  'parent': ['Family', 'Parenting', 'Childhood'],
  'mom': ['Family', 'Parenting'],
  'dad': ['Family', 'Parenting'],
  'friend': ['Friendship', 'Relationships', 'Loneliness'],
  'breakup': ['Divorce', 'Relationships', 'Grief', 'Loss'],
  'death': ['Grief', 'Loss of a Loved One', 'Loss'],
  'sleep': ['Sleep', 'Anxiety'],
  'panic': ['Panic', 'Anxiety'],
  'attack': ['Panic', 'Anxiety'],
}

// ==================== QUICK FILTERS ====================
// Subsets of topics for quick filtering UI

export const QUICK_EMOTION_FILTERS = [
  'Anxiety', 'Sadness', 'Anger', 'Grief', 'Shame', 'Loneliness',
  'Fear', 'Overwhelm', 'Guilt', 'Frustration',
] as const

export const QUICK_CONTEXT_FILTERS = [
  'Relationships', 'Work', 'Family', 'Self-Worth', 'Health',
  'Life Transitions', 'Trauma', 'Identity',
] as const

// ==================== RESOURCE TYPES ====================

export const RESOURCE_TYPES = ['PDF', 'VIDEO', 'EXTERNAL_LINK', 'PAGE'] as const

export type ResourceType = typeof RESOURCE_TYPES[number]

// ==================== STATUS ====================

export const RESOURCE_STATUSES = ['DRAFT', 'PENDING', 'PUBLISHED', 'ARCHIVED'] as const

export type ResourceStatus = typeof RESOURCE_STATUSES[number]

export const STATUS_CONFIG = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending Review' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const

// ==================== AUDIENCE ====================

export const AUDIENCES = ['THERAPIST', 'CLIENT', 'BOTH'] as const

export type Audience = typeof AUDIENCES[number]
